"use client";

/**
 * Hook that answers the single question every Cortex surface needs:
 *   "Does the current user have credentials configured so they can chat?"
 *
 * The status is derived server-side via get_my_ai_credential_status() so it
 * cannot be bypassed by tampering with localStorage. It is refreshed on mount,
 * on auth changes, and when callers call refetch().
 */

import { useCallback, useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { AICredentialStatus, UserAISettings } from "@flowstate/types";

export type CredentialReadiness =
  | "loading"
  | "ready"
  | "needs_setup";

export interface UseCortexCredentialStatusReturn {
  /** Derived server-side credential status */
  status: AICredentialStatus;
  /** Human-readable readiness state */
  readiness: CredentialReadiness;
  /** True when status is one of the usable credential states */
  isReady: boolean;
  /** True while the initial server lookup is in flight */
  isLoading: boolean;
  /** Last error, if any */
  error: string | null;
  /** Force a refresh from the server */
  refetch: () => void;
}

function isCredentialStatus(value: unknown): value is AICredentialStatus {
  return (
    typeof value === "string" &&
    ["none", "gemini_oauth", "gemini_byok", "openrouter_byok", "openrouter_platform"].includes(
      value,
    )
  );
}

function isReadyStatus(status: AICredentialStatus): boolean {
  return status !== "none";
}

export function useCortexCredentialStatus(
  supabase: SupabaseClient | null,
  localSettings?: Pick<UserAISettings, "provider" | "aiCredentialStatus">,
  allowOpenRouterPlatform = false,
): UseCortexCredentialStatusReturn {
  const [status, setStatus] = useState<AICredentialStatus>(
    localSettings?.aiCredentialStatus ?? "none",
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!supabase) {
        setStatus("none");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const { data, error: rpcError } = await supabase.rpc(
          "get_my_ai_credential_status" as never,
        );

        if (cancelled) return;

        if (rpcError) {
          console.warn("[useCortexCredentialStatus] RPC failed:", rpcError.message);
          setError(rpcError.message);
          // Fall back to local settings if available
          setStatus(localSettings?.aiCredentialStatus ?? "none");
          return;
        }

        const serverStatus = isCredentialStatus(data) ? data : "none";

        // If the user explicitly selected OpenRouter with the platform key,
        // treat that as ready even though the server cannot know whether the
        // platform OPENROUTER_API_KEY is configured.
        if (
          allowOpenRouterPlatform &&
          serverStatus === "none" &&
          localSettings?.provider === "openrouter"
        ) {
          setStatus("openrouter_platform");
        } else {
          setStatus(serverStatus);
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        console.warn("[useCortexCredentialStatus] Unexpected error:", message);
        setError(message);
        setStatus(localSettings?.aiCredentialStatus ?? "none");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [supabase, tick, localSettings?.provider, localSettings?.aiCredentialStatus, allowOpenRouterPlatform]);

  const readiness = isLoading ? "loading" : isReadyStatus(status) ? "ready" : "needs_setup";

  return {
    status,
    readiness,
    isReady: readiness === "ready",
    isLoading,
    error,
    refetch,
  };
}
